import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Bug, AlertTriangle, Pencil, X as Xicon, FileText, Image as ImageIcon, Film, Save, Download } from "lucide-react";
import FileUpload from "@/components/FileUpload";
import TesterDeviceInput from "@/components/TesterDeviceInput";

const SEV = ["critical", "high", "medium", "low"];

/**
 * IssueDrawer — right-side sheet that shows the full detail of a linked issue.
 *
 * Props:
 *  - issueId: string | null  (null => drawer closed)
 *  - onClose: () => void
 *  - taskStatus: string      (used to determine if a reporter is allowed to edit)
 *  - currentUser: { id, role }
 *  - onChange: optional callback fired when the issue is updated (parent can refresh)
 */
export default function IssueDrawer({ issueId, onClose, taskStatus, currentUser, onChange }) {
  const [loading, setLoading] = useState(false);
  const [issue, setIssue] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    if (!issueId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/issues/${issueId}`);
      setIssue(data);
      setDraft({
        title: data.title || "",
        steps_to_reproduce: data.steps_to_reproduce || "",
        expected_result: data.expected_result || "",
        actual_result: data.actual_result || "",
        severity: data.severity || "medium",
        priority: data.priority || "medium",
        environment: data.environment || "",
        device_browser: data.device_browser || "",
        attachments: data.attachments || [],
      });
    } catch {
      toast.error("Failed to load issue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!issueId) { setIssue(null); setEditing(false); return; }
    reload();
  }, [issueId]);

  if (!issueId) return null;

  const isReporter = currentUser?.role === "freelancer" && issue?.reporter_id === currentUser?.id;
  const isAdminOrManager = currentUser?.role === "admin" || currentUser?.role === "manager";

  // Reporter can edit when the issue is in a reporter-editable status OR their task is In Progress.
  const reporterCanEdit = isReporter && (
    issue && (["New", "Needs More Info"].includes(issue.status) || taskStatus === "In Progress")
  );
  const canEdit = reporterCanEdit || isAdminOrManager;

  const save = async () => {
    if (!issue) return;
    setSaving(true);
    try {
      await api.patch(`/issues/${issue.id}`, draft);
      toast.success(issue.status === "Needs More Info" ? "Updated and resubmitted for review" : "Issue updated");
      setEditing(false);
      await reload();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    if (issue) {
      setDraft({
        title: issue.title || "",
        steps_to_reproduce: issue.steps_to_reproduce || "",
        expected_result: issue.expected_result || "",
        actual_result: issue.actual_result || "",
        severity: issue.severity || "medium",
        priority: issue.priority || "medium",
        environment: issue.environment || "",
        device_browser: issue.device_browser || "",
        attachments: issue.attachments || [],
      });
    }
  };

  return (
    <Sheet open={!!issueId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0 gap-0" data-testid="issue-drawer">
        <SheetHeader className="p-5 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="flex items-center gap-2 text-slate-900">
                <Bug className="w-4 h-4 text-cyan-700"/>
                {loading ? "Loading…" : (issue?.title || "Issue")}
              </SheetTitle>
              <SheetDescription>
                {issue?.suspected_duplicate_of && (
                  <span className="inline-flex items-center gap-1 text-amber-700 mr-2">
                    <AlertTriangle className="w-3.5 h-3.5"/> Possible duplicate
                  </span>
                )}
                {issue?.status && <span className="pill bg-slate-100 text-slate-700 border-slate-200 mr-1">{issue.status}</span>}
                {issue?.severity && <span className={`pill ${
                  issue.severity === "critical" ? "bg-red-50 text-red-700 border-red-200" :
                  issue.severity === "high" ? "bg-rose-50 text-rose-700 border-rose-200" :
                  issue.severity === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-slate-50 text-slate-600 border-slate-200"
                }`}>{issue.severity}</span>}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {canEdit && !editing && (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="btn-edit-issue-drawer">
                  <Pencil className="w-3.5 h-3.5 mr-1.5"/>Edit
                </Button>
              )}
            </div>
          </div>
          {reporterCanEdit && issue?.status === "Needs More Info" && !editing && (
            <div className="mt-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="needs-more-info-hint">
              A reviewer needs more info on this issue. Edit and save to resubmit it for review.
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading || !issue ? (
            <div className="text-sm text-slate-500">Loading issue…</div>
          ) : editing ? (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} data-testid="drawer-input-title"/></div>
              <div><Label>Steps to reproduce</Label><Textarea rows={4} value={draft.steps_to_reproduce} onChange={e => setDraft(d => ({ ...d, steps_to_reproduce: e.target.value }))} data-testid="drawer-input-steps"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Expected</Label><Textarea rows={2} value={draft.expected_result} onChange={e => setDraft(d => ({ ...d, expected_result: e.target.value }))} data-testid="drawer-input-expected"/></div>
                <div><Label>Actual</Label><Textarea rows={2} value={draft.actual_result} onChange={e => setDraft(d => ({ ...d, actual_result: e.target.value }))} data-testid="drawer-input-actual"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Severity</Label>
                  <Select value={draft.severity} onValueChange={v => setDraft(d => ({ ...d, severity: v }))}>
                    <SelectTrigger data-testid="drawer-select-severity"><SelectValue/></SelectTrigger>
                    <SelectContent>{SEV.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={draft.priority} onValueChange={v => setDraft(d => ({ ...d, priority: v }))}>
                    <SelectTrigger data-testid="drawer-select-priority"><SelectValue/></SelectTrigger>
                    <SelectContent>{SEV.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Environment</Label><Input value={draft.environment} onChange={e => setDraft(d => ({ ...d, environment: e.target.value }))} data-testid="drawer-input-env"/></div>
                <div><Label>Device / browser</Label><TesterDeviceInput value={draft.device_browser} onChange={v => setDraft(d => ({ ...d, device_browser: v }))} testid="drawer-input-device"/></div>
              </div>
              <div>
                <Label>Attachments (screenshot / log / video)</Label>
                {draft.attachments?.length > 0 && (
                  <ul className="mb-2 space-y-1" data-testid="drawer-attachments-editing">
                    {draft.attachments.map((a, idx) => (
                      <li key={idx} className="flex items-center justify-between text-xs border border-slate-100 rounded p-1.5">
                        <span className="truncate">{a.name || a.filename || a.id}</span>
                        <button
                          type="button"
                          onClick={() => setDraft(d => ({ ...d, attachments: d.attachments.filter((_, i) => i !== idx) }))}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`drawer-remove-attachment-${idx}`}
                        >
                          <Xicon className="w-3.5 h-3.5"/>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <FileUpload testid="drawer-upload-attachment" onUploaded={(d) => setDraft(dd => ({ ...dd, attachments: [...(dd.attachments || []), d] }))}/>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <Section label="Steps to reproduce" value={issue.steps_to_reproduce}/>
              <div className="grid grid-cols-2 gap-3">
                <Section label="Expected" value={issue.expected_result}/>
                <Section label="Actual" value={issue.actual_result}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Section label="Environment" value={issue.environment || "—"}/>
                <Section label="Device / browser" value={issue.device_browser || "—"}/>
              </div>
              <Attachments attachments={issue.attachments}/>
              <div className="text-[11px] text-slate-500">
                Reported by <b>{issue.reporter_name || issue.reporter_email || "—"}</b>
                {issue.created_at && <> · {new Date(issue.created_at).toLocaleString()}</>}
              </div>
            </div>
          )}
        </div>

        {editing && (
          <div className="border-t border-slate-200 p-3 flex items-center justify-end gap-2 bg-white">
            <Button variant="ghost" onClick={cancelEdit} disabled={saving} data-testid="drawer-btn-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="drawer-btn-save">
              <Save className="w-3.5 h-3.5 mr-1.5"/>{saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-slate-800 whitespace-pre-wrap break-words">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function Attachments({ attachments }) {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (!Array.isArray(attachments) || attachments.length === 0) {
    return <Section label="Attachments" value="No attachments."/>;
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Attachments ({attachments.length})</div>
      <div className="grid grid-cols-2 gap-2" data-testid="drawer-attachments">
        {attachments.map((a, idx) => {
          const mime = (a.mime || a.content_type || "").toLowerCase();
          const isImage = mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.name || "");
          const isVideo = mime.startsWith("video/") || /\.(mp4|mov|webm|m4v|avi)$/i.test(a.name || "");
          const url = a.id ? `${API}/files/${a.id}` : a.url;
          const preview = isImage || isVideo;
          const commonCls = "border border-slate-200 rounded overflow-hidden hover:border-cyan-400 transition text-left w-full";
          const inner = (
            <>
              {isImage && url ? (
                <img src={url} alt={a.name || `attachment ${idx + 1}`} className="w-full h-24 object-cover bg-slate-100"/>
              ) : isVideo && url ? (
                <video src={url} className="w-full h-24 object-cover bg-slate-900" muted playsInline preload="metadata"/>
              ) : (
                <div className="h-24 flex items-center justify-center bg-slate-50 text-slate-400">
                  {isImage ? <ImageIcon className="w-6 h-6"/> : isVideo ? <Film className="w-6 h-6"/> : <FileText className="w-6 h-6"/>}
                </div>
              )}
              <div className="px-2 py-1 text-[11px] text-slate-600 truncate">{a.name || a.filename || `File ${idx + 1}`}</div>
            </>
          );
          return preview && url ? (
            <button
              key={idx}
              type="button"
              onClick={() => setLightbox({ url, name: a.name || a.filename || `File ${idx + 1}`, isVideo })}
              className={commonCls}
              data-testid={`drawer-att-${idx}`}
            >
              {inner}
            </button>
          ) : (
            <a
              key={idx}
              href={url}
              target="_blank"
              rel="noreferrer"
              className={commonCls + " block"}
              data-testid={`drawer-att-${idx}`}
            >
              {inner}
            </a>
          );
        })}
      </div>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="drawer-lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2"
            aria-label="Close preview"
            data-testid="drawer-lightbox-close"
          >
            <Xicon className="w-5 h-5"/>
          </button>
          <a
            href={lightbox.url}
            download={lightbox.name}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 right-16 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 inline-flex items-center gap-1.5 text-xs"
            data-testid="drawer-lightbox-download"
          >
            <Download className="w-4 h-4"/> Download
          </a>
          <div className="max-w-6xl max-h-[90vh] w-full flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {lightbox.isVideo ? (
              <video src={lightbox.url} controls autoPlay className="max-h-[85vh] max-w-full rounded-md shadow-2xl bg-black" data-testid="drawer-lightbox-video"/>
            ) : (
              <img src={lightbox.url} alt={lightbox.name} className="max-h-[85vh] max-w-full object-contain rounded-md shadow-2xl" data-testid="drawer-lightbox-image"/>
            )}
            <div className="text-xs text-white/80 truncate max-w-full text-center">{lightbox.name}</div>
          </div>
        </div>
      )}
    </div>
  );
}
